import { useState, useEffect, useRef, useCallback } from 'react';
import { api, getErrorMessage } from '@/App';
import { toast } from 'sonner';

// Import POS sub-components
import {
  RestaurantSelector,
  POSLockScreen,
  POSPinSetup,
  MenuGrid,
  OrderCart,
  PaymentDialog,
  BillingDialog,
  TableSelectDialog,
  RoomSelectDialog,
  OrderTypeDialog,
  CreditCustomerDialog,
  ViewOrdersDialog,
  NotesDialog,
  SplitPayDialog,
  POSActionBar,
  RunningTablesBar
} from '@/components/pos';

export default function RestaurantPOS({ user, onLogout }) {
  // Restaurant selection
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  
  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Lock screen state
  const [isLocked, setIsLocked] = useState(false);
  const [needsPinSetup, setNeedsPinSetup] = useState(false);
  const [settingPin, setSettingPin] = useState(false);
  const [lockSettings, setLockSettings] = useState({ lock_timeout_minutes: 5, require_pin: true });
  const [unlocking, setUnlocking] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const lockTimerRef = useRef(null);
  
  // Current cart for new order
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('dine_in');
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('percent');
  const [persons, setPersons] = useState(1);
  const [staffName, setStaffName] = useState('');
  const [orderNumber, setOrderNumber] = useState(null);
  const [onlineOrderCount, setOnlineOrderCount] = useState(0);
  
  // Credit customer selection
  const [selectedCreditCustomer, setSelectedCreditCustomer] = useState(null);
  const [creditSearchQuery, setCreditSearchQuery] = useState('');
  const [creditSearchResults, setCreditSearchResults] = useState({ guests: [], credit_customers: [] });
  
  // Table running orders
  const [tableOrders, setTableOrders] = useState({});
  
  // Dialogs
  const [orderTypeDialogOpen, setOrderTypeDialogOpen] = useState(false);
  const [tableSelectDialogOpen, setTableSelectDialogOpen] = useState(false);
  const [roomSelectDialogOpen, setRoomSelectDialogOpen] = useState(false);
  const [viewOrdersDialogOpen, setViewOrdersDialogOpen] = useState(false);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [splitPayDialogOpen, setSplitPayDialogOpen] = useState(false);
  const [customerSelectDialogOpen, setCustomerSelectDialogOpen] = useState(false);
  
  // Selected table for billing
  const [billingTable, setBillingTable] = useState(null);
  const [billingOrders, setBillingOrders] = useState([]);
  
  // Bookings for room service
  const [bookings, setBookings] = useState([]);

  const TAX_RATE = 0.18;

  // Activity tracking for auto-lock
  const resetActivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Load POS settings and restaurants, check for PIN
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [settingsRes, restaurantsRes, hasPinRes] = await Promise.all([
          api.get('/pos-settings'),
          api.get('/restaurants'),
          api.get('/pos/has-pin')
        ]);
        setLockSettings(settingsRes.data);
        setRestaurants(restaurantsRes.data);
        
        // Check if user needs to set up PIN
        if (!hasPinRes.data.has_pin) {
          setNeedsPinSetup(true);
        }
        
        if (restaurantsRes.data.length === 1) {
          setSelectedRestaurant(restaurantsRes.data[0]);
        }
      } catch (e) {
        console.error('Failed to load initial data');
      }
    };
    loadInitialData();
  }, []);

  // Handle PIN setup
  const handleSetPin = async (pin) => {
    setSettingPin(true);
    try {
      await api.post('/pos/set-pin', { pin });
      setNeedsPinSetup(false);
      toast.success('POS PIN set successfully!');
      resetActivityTimer();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to set PIN'));
    } finally {
      setSettingPin(false);
    }
  };

  // Auto-lock timer
  useEffect(() => {
    const checkLock = () => {
      if (needsPinSetup) return; // Don't auto-lock during PIN setup
      const elapsed = (Date.now() - lastActivityRef.current) / 1000 / 60;
      if (lockSettings.lock_timeout_minutes > 0 && elapsed >= lockSettings.lock_timeout_minutes && !isLocked) {
        setIsLocked(true);
      }
    };

    lockTimerRef.current = setInterval(checkLock, 10000);
    return () => clearInterval(lockTimerRef.current);
  }, [lockSettings.lock_timeout_minutes, isLocked, needsPinSetup]);

  // Track user activity
  useEffect(() => {
    const handleActivity = () => resetActivityTimer();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, [resetActivityTimer]);

  const handleUnlock = async (method, credential, clearCredential) => {
    if (!credential) {
      toast.error('Please enter your PIN or password');
      return;
    }
    setUnlocking(true);
    try {
      await api.post('/pos/unlock', { method, credential });
      setIsLocked(false);
      clearCredential();
      resetActivityTimer();
      toast.success('POS unlocked');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid credentials'));
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    if (selectedRestaurant) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant]);

  const loadData = async () => {
    if (!selectedRestaurant) return;
    
    try {
      const [menuRes, bookingsRes, tablesRes] = await Promise.all([
        api.get(`/menu?restaurant_id=${selectedRestaurant.id}`),
        api.get('/bookings?status=checked_in'),
        api.get(`/tables?restaurant_id=${selectedRestaurant.id}`)
      ]);
      setMenuItems(menuRes.data.filter(item => item.is_available));
      setBookings(bookingsRes.data);
      setTables(tablesRes.data);
      
      await loadTableOrders(tablesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTableOrders = async (tableList) => {
    const orders = {};
    // Load orders for all tables, not just occupied ones
    // This handles cases where table status might be out of sync
    for (const table of tableList) {
      try {
        const res = await api.get(`/orders/table/${table.id}`);
        // Filter out completed and cancelled orders - only show active orders
        const activeOrders = res.data.filter(o => 
          !['completed', 'cancelled'].includes(o.status)
        );
        if (activeOrders.length > 0) {
          orders[table.id] = activeOrders;
          // If table has active orders but status is not occupied, update it
          if (table.status !== 'occupied') {
            try {
              await api.patch(`/tables/${table.id}`, { status: 'occupied' });
            } catch (e) {
              // Ignore status update errors
            }
          }
        }
      } catch (e) {
        // Table may not have orders - ignore
      }
    }
    setTableOrders(orders);
  };

  // Refresh table orders (called after order status changes)
  const refreshTableOrders = async () => {
    if (tables.length > 0) {
      // Reload tables to get updated statuses
      try {
        const tablesRes = await api.get(`/tables?restaurant_id=${selectedRestaurant.id}`);
        setTables(tablesRes.data);
        await loadTableOrders(tablesRes.data);
      } catch (e) {
        console.error('Failed to refresh table orders');
      }
    }
  };

  // Categories
  const categories = ['all', ...new Set(menuItems.map(item => item.category))];

  // Cart operations
  const addToCart = (item) => {
    const existing = cart.find(c => c.item_id === item.id);
    if (existing) {
      setCart(cart.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { item_id: item.id, item_name: item.name, quantity: 1, price: item.price }]);
    }
    toast.success(`${item.name} added`, { position: 'top-center', duration: 800 });
  };

  const updateQuantity = (itemId, delta) => {
    setCart(cart.map(c => c.item_id === itemId ? { ...c, quantity: Math.max(1, c.quantity + delta) } : c));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.item_id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setDiscount(0);
    setOrderNotes('');
    setOrderNumber(null);
    setCustomerName('');
    setCustomerPhone('');
    setPersons(1);
  };

  // Generate new order number
  const generateOrderNumber = () => {
    const num = Math.floor(1000 + Math.random() * 9000);
    setOrderNumber(num);
  };

  // New Order - clears cart and generates new order number
  const handleNewOrder = () => {
    clearCart();
    setSelectedTable(null);
    setSelectedBooking(null);
    setOrderType('dine_in');
    generateOrderNumber();
    toast.success('New order started!', { position: 'top-center' });
  };

  // Cancel Order
  const handleCancelOrder = () => {
    if (cart.length === 0) {
      toast.info('No active order to cancel', { position: 'top-center' });
      return;
    }
    clearCart();
    setSelectedTable(null);
    setSelectedBooking(null);
    toast.success('Order cancelled', { position: 'top-center' });
  };

  // Send to Kitchen (KOT)
  const handleSendToKitchen = async () => {
    await handlePlaceOrder(false);
  };

  // Save and Print
  const handleSaveAndPrint = async () => {
    await handlePlaceOrder(false);
    toast.info('Print functionality coming soon.', { position: 'top-center' });
  };

  // KOT and Print
  const handleKitchenAndPrint = async () => {
    await handlePlaceOrder(false);
    toast.info('Print functionality coming soon.', { position: 'top-center' });
  };

  // Select Room for room service
  const handleSelectRoom = (booking) => {
    setSelectedBooking(booking);
    setCustomerName(booking.guest_name || 'Room Guest');
    setRoomSelectDialogOpen(false);
  };

  // Calculations
  const calculateSubtotal = () => cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    return discountType === 'percent' ? subtotal * (discount / 100) : Math.min(discount, subtotal);
  };
  const calculateTax = () => (calculateSubtotal() - calculateDiscount()) * TAX_RATE;
  const calculateTotal = () => calculateSubtotal() - calculateDiscount() + calculateTax();

  const getOrderTypeLabel = (type) => {
    switch(type) {
      case 'room_service': return 'Room Service';
      case 'dine_in': return 'Dine In';
      case 'takeaway': return 'Takeaway';
      default: return 'Dine In';
    }
  };

  // Handle Credit Payment
  const handleCreditPayment = async (customerId) => {
    if (cart.length === 0) {
      toast.error('Cart is empty', { position: 'top-center' });
      return;
    }

    try {
      const orderData = {
        restaurant_id: selectedRestaurant?.id || null,
        order_type: orderType,
        table_id: selectedTable?.id || null,
        table_number: selectedTable?.table_number || null,
        booking_id: selectedBooking?.id || null,
        customer_name: customerName,
        items: cart.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: calculateSubtotal(),
        discount: calculateDiscount(),
        tax: calculateTax(),
        notes: orderNotes
      };

      const orderResponse = await api.post('/orders', orderData);
      const orderId = orderResponse.data.id;
      
      const chargeResponse = await api.post('/credit/charge', {
        customer_id: customerId,
        amount: calculateTotal(),
        description: `Restaurant Order #${orderId.slice(-6).toUpperCase()}`,
        reference_type: 'order',
        reference_id: orderId
      });
      
      await api.patch(`/orders/${orderId}/payment?payment_status=paid&payment_method=credit`);
      
      toast.success(
        `Order charged to credit! Available credit: ₹${chargeResponse.data.available_credit?.toLocaleString()}`,
        { position: 'top-center', duration: 4000 }
      );
      
      clearCart();
      setPaymentDialogOpen(false);
      setSelectedCreditCustomer(null);
      setCreditSearchQuery('');
      setCreditSearchResults({ guests: [], credit_customers: [] });
      loadData();
    } catch (error) {
      console.error('Credit payment failed:', error);
      toast.error(error.response?.data?.detail || 'Credit payment failed', { position: 'top-center' });
    }
  };

  // Place Order
  const handlePlaceOrder = async (payNow = false, paymentMethod = null, paymentStatus = 'unpaid') => {
    if (cart.length === 0) {
      toast.error('Cart is empty', { position: 'top-center' });
      return;
    }

    // For dine-in, require table selection
    if (orderType === 'dine_in' && !selectedTable) {
      toast.error('Please select a table first', { position: 'top-center' });
      setTableSelectDialogOpen(true);
      return;
    }

    // For room service, require room selection
    if (orderType === 'room_service' && !selectedBooking) {
      toast.error('Please select a room first', { position: 'top-center' });
      setRoomSelectDialogOpen(true);
      return;
    }

    try {
      const orderData = {
        restaurant_id: selectedRestaurant?.id || null,
        order_type: orderType,
        table_id: selectedTable?.id || null,
        table_number: selectedTable?.table_number || null,
        booking_id: selectedBooking?.id || null,
        room_id: selectedBooking?.room_id || null,
        room_number: selectedBooking?.room_number || selectedBooking?.room_id || null,
        customer_name: customerName || 'Walk-in Customer',
        customer_phone: customerPhone || null,
        items: cart.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: calculateSubtotal(),
        discount: calculateDiscount(),
        tax: calculateTax(),
        notes: orderNotes
      };

      const response = await api.post('/orders', orderData);
      
      if (payNow && paymentMethod) {
        await api.patch(`/orders/${response.data.id}/payment?payment_status=${paymentStatus}&payment_method=${paymentMethod}`);
      }
      
      const msg = payNow 
        ? (paymentStatus === 'posted_to_room' ? 'Order posted to room bill!' : 'Order completed!')
        : `Order #${response.data.id.slice(-6).toUpperCase()} placed successfully!`;
      
      toast.success(msg, { position: 'top-center' });
      clearCart();
      setPaymentDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to place order', { position: 'top-center' });
    }
  };

  // Open billing dialog for a table
  const openBillingDialog = async (table) => {
    try {
      const res = await api.get(`/orders/table/${table.id}`);
      setBillingTable(table);
      setBillingOrders(res.data);
      setBillDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load orders');
    }
  };

  // Generate and pay bill for table
  const handleGenerateBill = async (paymentMethod) => {
    if (!billingTable || billingOrders.length === 0) return;
    
    try {
      await api.post(`/orders/bill-table/${billingTable.id}?payment_method=${paymentMethod}`);
      toast.success('Bill generated and paid!', { position: 'top-center' });
      setBillDialogOpen(false);
      setBillingTable(null);
      setBillingOrders([]);
      setSelectedTable(null);
      loadData();
    } catch (error) {
      toast.error('Failed to generate bill', { position: 'top-center' });
    }
  };

  // Select table and load its orders
  const selectTable = async (table) => {
    setSelectedTable(table);
    setOrderType('dine_in');
    setTableSelectDialogOpen(false);
    
    // Don't auto-open orders dialog, just select the table
    // User can click on table in Active Tables bar to see orders
  };

  // Handle restaurant change
  const handleRestaurantChange = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setLoading(true);
    setMenuItems([]);
    setTables([]);
    setTableOrders({});
    setCart([]);
    setSelectedTable(null);
  };

  // Credit customer search
  const searchCreditCustomers = async (query) => {
    try {
      const res = await api.get(`/credit/search-all?search=${encodeURIComponent(query)}`);
      setCreditSearchResults(res.data);
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  // Handle guest selection for credit
  const handleSelectGuestForCredit = async (guest) => {
    let customerId = guest.credit_customer_id;
    if (!guest.has_credit_account) {
      try {
        const res = await api.post('/credit/customers', {
          name: guest.name,
          phone: guest.phone || '',
          email: guest.email || '',
          guest_id: guest.id
        });
        customerId = res.data.customer.id;
        await api.post('/credit/accounts', {
          customer_id: customerId,
          credit_limit: 10000
        });
      } catch (err) {
        if (err.response?.data?.detail?.includes('already exists')) {
          // Customer already exists - look up by phone
          try {
            const searchRes = await api.get(`/credit/customers?search=${encodeURIComponent(guest.phone)}`);
            const existingCustomer = searchRes.data.find(c => c.phone === guest.phone);
            if (existingCustomer) {
              customerId = existingCustomer.id;
            } else {
              toast.error('Could not find existing credit customer');
              return;
            }
          } catch (searchErr) {
            toast.error('Failed to find credit customer');
            return;
          }
        } else {
          toast.error('Failed to create credit account');
          return;
        }
      }
    }
    if (!customerId) {
      toast.error('No credit customer ID found');
      return;
    }
    setSelectedCreditCustomer({ ...guest, credit_customer_id: customerId });
    setCustomerSelectDialogOpen(false);
    handleCreditPayment(customerId);
  };

  // Handle credit customer selection
  const handleSelectCreditCustomer = (customer) => {
    setSelectedCreditCustomer(customer);
    setCustomerSelectDialogOpen(false);
    handleCreditPayment(customer.id);
  };

  // Check if opened in popup mode
  const isPopup = new URLSearchParams(window.location.search).get('popup') === 'true';

  // PIN Setup Screen - shown when user has no PIN set
  if (needsPinSetup) {
    return (
      <POSPinSetup
        onSetPin={handleSetPin}
        saving={settingPin}
        isPopup={isPopup}
        onLogout={onLogout}
        userName={user?.name}
      />
    );
  }

  // Restaurant Selection Screen
  if (!selectedRestaurant) {
    return (
      <RestaurantSelector
        restaurants={restaurants}
        onSelectRestaurant={handleRestaurantChange}
        isPopup={isPopup}
        onLogout={onLogout}
      />
    );
  }

  // Lock Screen
  if (isLocked) {
    return (
      <POSLockScreen
        onUnlock={handleUnlock}
        unlocking={unlocking}
        isPopup={isPopup}
        onLogout={onLogout}
      />
    );
  }

  return (
    <div className={`${isPopup ? 'h-screen' : 'h-[calc(100vh-120px)]'} flex flex-col bg-gradient-hero`} data-testid="restaurant-pos">
      {/* Top Action Bar */}
      <POSActionBar
        isPopup={isPopup}
        selectedRestaurant={selectedRestaurant}
        orderNumber={orderNumber}
        onlineOrderCount={onlineOrderCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onNewOrder={handleNewOrder}
        onCancelOrder={handleCancelOrder}
        onOpenOnlineOrders={() => toast.info('Online orders coming soon!')}
        onLock={() => setIsLocked(true)}
        onLogout={onLogout}
        onChangeRestaurant={() => setSelectedRestaurant(null)}
      />

      {/* Running Tables Bar */}
      <RunningTablesBar
        tableOrders={tableOrders}
        tables={tables}
        restaurantId={selectedRestaurant?.id}
        onOpenBilling={openBillingDialog}
        onGenerateBill={openBillingDialog}
        onRefreshOrders={refreshTableOrders}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Menu Grid with Category Sidebar */}
        <MenuGrid
          menuItems={menuItems}
          cart={cart}
          selectedCategory={selectedCategory}
          categories={categories}
          onSelectCategory={setSelectedCategory}
          searchQuery={searchQuery}
          loading={loading}
          onAddToCart={addToCart}
        />

        {/* Cart Panel */}
        <OrderCart
          cart={cart}
          orderType={orderType}
          selectedTable={selectedTable}
          selectedRoom={selectedBooking}
          tableOrders={tableOrders}
          discount={discount}
          discountType={discountType}
          customerName={customerName}
          customerPhone={customerPhone}
          persons={persons}
          staffName={staffName}
          onUpdateQuantity={updateQuantity}
          onRemoveFromCart={removeFromCart}
          onDiscountChange={setDiscount}
          onDiscountTypeChange={setDiscountType}
          onCustomerNameChange={setCustomerName}
          onCustomerPhoneChange={setCustomerPhone}
          onPersonsChange={setPersons}
          onStaffChange={setStaffName}
          onOrderTypeChange={setOrderType}
          onOpenTableSelect={() => setTableSelectDialogOpen(true)}
          onOpenRoomSelect={() => setRoomSelectDialogOpen(true)}
          onPlaceOrder={handlePlaceOrder}
          onSaveAndPrint={handleSaveAndPrint}
          onSendToKitchen={handleSendToKitchen}
          onKitchenAndPrint={handleKitchenAndPrint}
          onOpenBilling={openBillingDialog}
          onOpenPayment={() => setPaymentDialogOpen(true)}
          staffList={['Staff 1', 'Staff 2', 'Staff 3']}
          TAX_RATE={TAX_RATE}
        />
      </div>

      {/* Dialogs */}
      <OrderTypeDialog
        open={orderTypeDialogOpen}
        onOpenChange={setOrderTypeDialogOpen}
        orderType={orderType}
        setOrderType={setOrderType}
        selectedTable={selectedTable}
        setSelectedTable={setSelectedTable}
        selectedBooking={selectedBooking}
        setSelectedBooking={setSelectedBooking}
        customerName={customerName}
        setCustomerName={setCustomerName}
        tables={tables}
        tableOrders={tableOrders}
        bookings={bookings}
      />

      <TableSelectDialog
        open={tableSelectDialogOpen}
        onOpenChange={setTableSelectDialogOpen}
        tables={tables}
        tableOrders={tableOrders}
        selectedTable={selectedTable}
        onSelectTable={selectTable}
        onOpenBilling={openBillingDialog}
      />

      <RoomSelectDialog
        open={roomSelectDialogOpen}
        onOpenChange={setRoomSelectDialogOpen}
        bookings={bookings}
        selectedBooking={selectedBooking}
        onSelectBooking={handleSelectRoom}
      />

      <ViewOrdersDialog
        open={viewOrdersDialogOpen}
        onOpenChange={setViewOrdersDialogOpen}
        selectedTable={selectedTable}
        tableOrders={tableOrders}
        onOpenBilling={openBillingDialog}
      />

      <BillingDialog
        open={billDialogOpen}
        onOpenChange={setBillDialogOpen}
        billingTable={billingTable}
        billingOrders={billingOrders}
        onGenerateBill={handleGenerateBill}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        orderType={orderType}
        selectedCreditCustomer={selectedCreditCustomer}
        onClearCreditCustomer={() => setSelectedCreditCustomer(null)}
        onPlaceOrder={handlePlaceOrder}
        onOpenCreditCustomerSelect={() => setCustomerSelectDialogOpen(true)}
        calculateSubtotal={calculateSubtotal}
        calculateDiscount={calculateDiscount}
        calculateTax={calculateTax}
        calculateTotal={calculateTotal}
      />

      <CreditCustomerDialog
        open={customerSelectDialogOpen}
        onOpenChange={setCustomerSelectDialogOpen}
        searchQuery={creditSearchQuery}
        setSearchQuery={setCreditSearchQuery}
        searchResults={creditSearchResults}
        onSearchCustomers={searchCreditCustomers}
        onSelectGuest={handleSelectGuestForCredit}
        onSelectCreditCustomer={handleSelectCreditCustomer}
      />

      <NotesDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        orderNotes={orderNotes}
        setOrderNotes={setOrderNotes}
      />

      <SplitPayDialog
        open={splitPayDialogOpen}
        onOpenChange={setSplitPayDialogOpen}
        calculateTotal={calculateTotal}
      />
    </div>
  );
}
